/**
 * GoogleAd - Google AdSense 광고 컴포넌트 (고도화 버전)
 * 
 * 사용법:
 * <GoogleAd slot="1234567890" format="auto" />
 * <GoogleAd slot="9876543210" format="rectangle" layout="side-rail" />
 * <GoogleAd slot="1111111111" format="horizontal" lazyLoad />
 * 
 * 환경변수 필요: VITE_GOOGLE_AD_CLIENT 또는 VITE_ADSENSE_PUBLISHER_ID
 */

import { useEffect, useRef, useState } from 'react';
import { getAdSenseClientId, getAdSenseSlotId } from '@/lib/adsense-config';

interface GoogleAdProps {
  slot: string;
  format?: 'auto' | 'rectangle' | 'horizontal' | 'vertical' | 'fluid';
  layout?: 'default' | 'side-rail' | 'inline';
  responsive?: boolean;
  lazyLoad?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function GoogleAd({
  slot,
  format = 'auto',
  layout = 'default',
  responsive = true,
  lazyLoad = true,
  className = '',
  style = {},
}: GoogleAdProps) {
  const clientId = getAdSenseClientId();
  const resolvedSlot = getAdSenseSlotId(slot);
  const adRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(!lazyLoad);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Lazy loading using IntersectionObserver
  useEffect(() => {
    if (!lazyLoad || !adRef.current) {
      setIsVisible(true);
      return;
    }

    // Safety guard: Check if IntersectionObserver is supported
    const supported = typeof window !== 'undefined' && 'IntersectionObserver' in window;
    
    if (!supported) {
      // Fallback: Load immediately if IntersectionObserver not supported
      console.warn('[GoogleAd] IntersectionObserver not supported, loading ad immediately');
      setIsVisible(true);
      setHasLoaded(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoaded) {
            setIsVisible(true);
            setHasLoaded(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '200px', // Load 200px before entering viewport
        threshold: 0.01,
      }
    );

    observer.observe(adRef.current);

    return () => {
      observer.disconnect();
    };
  }, [lazyLoad, hasLoaded]);

  // Push ad to AdSense when visible
  useEffect(() => {
    if (!isVisible || !clientId || !resolvedSlot) return;

    const timer = setTimeout(() => {
      try {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
      } catch (error) {
        console.error('[GoogleAd] Error pushing ad:', error);
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, [isVisible, clientId, resolvedSlot]);

  if (!clientId || !resolvedSlot) {
    return import.meta.env.DEV ? (
      <div className="rounded-md border border-dashed border-muted-foreground/30 p-4 text-center text-sm text-muted-foreground">
        AdSense 클라이언트와 숫자형 광고 슬롯 설정 필요
      </div>
    ) : null;
  }

  // Layout-specific styles
  const layoutStyles: Record<string, string> = {
    default: '',
    'side-rail': 'sticky top-20 max-w-[300px]',
    inline: 'my-4',
  };

  return (
    <div 
      ref={adRef}
      className={`${layoutStyles[layout]} ${className}`} 
      style={style}
      data-testid="google-ad"
    >
      {isVisible ? (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', ...style }}
          data-ad-client={clientId}
          data-ad-slot={resolvedSlot}
          data-ad-format={format}
          data-full-width-responsive={responsive ? 'true' : 'false'}
        />
      ) : (
        <div 
          className="rounded-md border border-dashed border-muted-foreground/20 p-8 text-center text-sm text-muted-foreground bg-muted/30"
          style={{ minHeight: '250px' }}
        >
          광고 준비 중...
        </div>
      )}
    </div>
  );
}

export default GoogleAd;
